import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Paperclip, Pencil, Plus, Receipt, RotateCcw, Split, Trash2, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { splitHref } from "@/lib/splits";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { api, errMsg, statusOf } from "@/lib/api";
import { BILL_STATUS, fmtDate, money } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, NoAccess, PageHeader, StatusPill } from "@/components/common";
import { BillForm } from "@/components/bills/BillForm";
import { StoredFile } from "@/components/bills/FilePreview";
import UploadBillDialog from "@/components/bills/UploadBillDialog";
import { useLiveRefresh } from "@/lib/live";
import EmailInbox, { InboxAddressCard, useInbox } from "@/components/inbox/EmailInbox";

function BillsInbox({ hub }) {
  const inbox = useInbox(hub.id);
  useEffect(() => { if (inbox.d && new URLSearchParams(window.location.search).get("inbox")) document.getElementById("bills-inbox")?.scrollIntoView({ behavior: "smooth" }); }, [inbox.d]);
  if (inbox.denied || !inbox.d) return null;
  return (
    <div id="bills-inbox" className="mt-8 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]" data-testid="bills-inbox">
      <EmailInbox hubId={hub.id} inbox={inbox} readOnly={hub.read_only} />
      <InboxAddressCard hubId={hub.id} inbox={inbox} canWrite={!hub.read_only} testid="bills-inbox-address" />
    </div>
  );
}

const GREEN = "var(--glow-money)";
const FILTERS = [["all", "All"], ["upcoming", "Upcoming"], ["overdue", "Overdue"], ["paid", "Paid"]];

function BillRow({ b, onPay, onEdit, onDelete, onOpen, ro, split }) {
  const s = BILL_STATUS[b.status];
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3.5 transition-colors duration-150 hover:bg-q-raised/40 sm:px-5" data-testid={`bill-row-${b.id}`}>
      <button onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid={`bill-open-${b.id}`}>
        <div className="flex items-center gap-2">
          <span className="truncate font-semibold text-q-text" data-testid="bill-vendor">{b.vendor}</span>
          {b.file && <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-q-faint" data-testid={`bill-has-file-${b.id}`} />}
        </div>
        <div className="mt-0.5 text-xs text-q-faint">
          {b.category} · due {fmtDate(b.due_date)}{b.paid && b.paid_date ? ` · paid ${fmtDate(b.paid_date)}` : ""}
        </div>
      </button>
      <StatusPill label={s.label} rgb={s.rgb} testid={`bill-status-${b.id}`} />
      <span className="w-24 text-right font-display font-bold tabular-nums text-q-text" data-testid={`bill-amount-${b.id}`}>{money(b.amount)}</span>
      <div className="flex items-center gap-1">
        {b.paid ? (
          <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" disabled={ro} onClick={onPay} data-testid={`bill-unpay-btn-${b.id}`} title="Mark unpaid"><RotateCcw className="h-3.5 w-3.5" /> Unpaid</button>
        ) : (
          <button className="q-btn px-2.5 py-1.5 text-xs" disabled={ro} onClick={onPay} data-testid={`bill-pay-btn-${b.id}`}
            style={{ color: `rgb(${GREEN})`, background: `rgba(${GREEN},0.1)`, boxShadow: `inset 0 0 0 1px rgba(${GREEN},0.3)` }}><Check className="h-3.5 w-3.5" /> Mark paid</button>
        )}
        {split && <Link to={split} className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" title="Split this" data-testid={`bill-split-btn-${b.id}`}><Split className="h-3.5 w-3.5" /> Split this</Link>}
        <button className="q-btn q-btn-ghost px-2 py-1.5" disabled={ro} onClick={onEdit} title="Edit" data-testid={`bill-edit-btn-${b.id}`}><Pencil className="h-3.5 w-3.5" /></button>
        <button className="q-btn q-btn-ghost px-2 py-1.5 hover:text-red-300" disabled={ro} onClick={onDelete} title="Delete" data-testid={`bill-delete-btn-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </li>
  );
}

export default function Bills() {
  const { hub, reloadHub } = useHub();
  const [bills, setBills] = useState(null);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(null);
  const [upload, setUpload] = useState(false);
  const [del, setDel] = useState(null);
  const [view, setView] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/organizations/${hub.id}/bills`);
      setBills(data.bills);
    } catch (e) {
      if (statusOf(e) === 403) setDenied(true);
    }
  }, [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), (m) => ["bill"].includes(m.entity), null);
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("bill");
    const b = !opened && id && bills?.find((x) => x.id === id);
    if (b) { setView(b); setOpened(true); }
  }, [bills, opened]);

  const changed = () => { load(); reloadHub(); };
  const counts = useMemo(() => {
    const c = { all: bills?.length || 0, upcoming: 0, overdue: 0, paid: 0 };
    (bills || []).forEach((b) => (c[b.status] += 1));
    return c;
  }, [bills]);

  if (denied) return <NoAccess />;
  if (!bills) return <Loader />;
  const shown = filter === "all" ? bills : bills.filter((b) => b.status === filter);

  const togglePaid = async (b) => {
    try {
      await api.post(`/organizations/${hub.id}/bills/${b.id}/${b.paid ? "unpay" : "pay"}`);
      toast.success(b.paid ? `${b.vendor} marked unpaid` : `${b.vendor} marked paid`);
      changed();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/organizations/${hub.id}/bills/${del.id}`);
      toast.success("Bill deleted");
      setDel(null);
      changed();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="bills-page">
      <PageHeader eyebrow="Money" title="Bills" rgb={GREEN} testid="bills-title" actions={<>
        <button className="q-btn q-btn-secondary" disabled={hub.read_only} onClick={() => setUpload(true)} data-testid="bills-upload-btn"><Upload className="h-4 w-4" /> Upload a bill</button>
        <button className="q-btn q-btn-primary" disabled={hub.read_only} onClick={() => setForm({})} data-testid="bills-add-btn"><Plus className="h-4 w-4" /> Add bill</button>
      </>} />
      <div className="mb-4 flex flex-wrap gap-2" role="tablist">
        {FILTERS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={filter === k} onClick={() => setFilter(k)} data-testid={`bills-filter-${k}`}
            className="rounded-full px-3.5 py-1.5 text-xs font-bold transition-[background-color,color,box-shadow] duration-150"
            style={filter === k ? { background: `rgba(${GREEN},0.16)`, color: `rgb(${GREEN})`, boxShadow: `inset 0 0 0 1px rgba(${GREEN},0.4)` } : { background: "rgb(var(--q-raised))", color: "rgb(var(--q-muted))" }}>
            {label} <span className="ml-1 opacity-70">{counts[k]}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <EmptyState Icon={Receipt} rgb={GREEN} testid="bills-empty"
          title={filter === "all" ? "No bills yet" : `No ${filter} bills`}
          hint={filter === "all" ? "Add your first bill, or upload a photo or PDF and we'll read it for you." : "Switch the filter to see your other bills."}
          action={filter === "all" && !hub.read_only && <button className="q-btn q-btn-primary" onClick={() => setUpload(true)}>Upload a bill</button>} />
      ) : (
        <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface" data-testid="bills-list">
          {shown.map((b) => (
            <BillRow key={b.id} b={b} onPay={() => togglePaid(b)} onEdit={() => setForm(b)} onDelete={() => setDel(b)} onOpen={() => setView(b)} ro={hub.read_only}
              split={hub.is_parent && !hub.read_only ? splitHref(hub.id, b.vendor, b.amount, b.paid_date || b.due_date) : null} />
          ))}
        </ul>
      )}
      <BillsInbox hub={hub} />

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-q-line bg-q-surface sm:max-w-lg" data-testid="bill-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">{form?.id ? "Edit bill" : "Add a bill"}</DialogTitle>
            <DialogDescription className="text-q-muted">Amounts are in USD.</DialogDescription>
          </DialogHeader>
          {form && <BillForm hubId={hub.id} initial={form} billId={form.id} fileId={form.file?.id}
            onSaved={() => { toast.success("Bill saved"); setForm(null); changed(); }} onCancel={() => setForm(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-q-line bg-q-surface sm:max-w-2xl" data-testid="bill-detail-dialog">
          {view && (<>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-extrabold">{view.vendor}</DialogTitle>
              <DialogDescription className="text-q-muted">{view.category} · due {fmtDate(view.due_date)}</DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-3">
              <span className="font-display text-3xl font-extrabold text-q-text" data-testid="bill-detail-amount">{money(view.amount)}</span>
              <StatusPill label={BILL_STATUS[view.status].label} rgb={BILL_STATUS[view.status].rgb} testid="bill-detail-status" />
            </div>
            {view.notes && <p className="text-sm text-q-muted">{view.notes}</p>}
            {view.file ? <StoredFile hubId={hub.id} file={view.file} /> : view.doc_id
              ? <Link to={`/h/${hub.id}/family/documents?doc=${view.doc_id}`} className="q-btn q-btn-secondary min-h-[44px]" data-testid="bill-detail-original"><Paperclip className="h-4 w-4" /> Open the emailed original in Documents</Link>
              : <p className="text-sm text-q-faint" data-testid="bill-detail-no-file">No file attached to this bill.</p>}
          </>)}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="border-q-line bg-q-surface" data-testid="bill-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete this bill?</AlertDialogTitle>
            <AlertDialogDescription className="text-q-muted">{del?.vendor} · {money(del?.amount)}. If it was paid, its spending entry goes too.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bill-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-500" data-testid="bill-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {upload && <UploadBillDialog open={upload} onOpenChange={setUpload} onSaved={changed} />}
    </div>
  );
}
